import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import Button from '../components/ui/Button';
import { Input, Select, Label } from '../components/ui/Input';
import DataGrid from '../components/grid/DataGrid';
import BulkEditBar from '../components/grid/BulkEditBar';
import BulkPreviewModal from '../components/grid/BulkPreviewModal';

const RANGING_CHANNELS = new Set(['WW', 'COLES']);

// Store Master (migrations 023/024) — the SQL system of record for retail
// stores across WW / Coles / Independents, plus the store × SKU ranging matrix.
// AG Grid (Community): the Stores view edits store attributes (PUT /stores/{ref})
// and bulk-edits via the governed /stores/import dry-run; the Ranging Matrix view
// renders /ranging/matrix as an editable boolean grid (per-cell PUT /ranging,
// bulk via /ranging/import). Excel round-trip preserved on both.

const CHANNELS = ['WW', 'COLES', 'INDEPENDENT'];

// Field groups for the edit drawer. Keys match the API's camelCase contract.
const GROUPS = [
  ['Location', [
    ['address', 'Address'], ['suburb', 'Suburb'], ['postcode', 'Postcode'],
    ['state', 'State'], ['phone', 'Phone'], ['latitude', 'Latitude'],
    ['longitude', 'Longitude'], ['region', 'Region'], ['tourismRegion', 'Tourism Region'],
  ]],
  ['Classification', [
    ['banner', 'Banner'], ['customerFacingName', 'Customer-Facing Name'],
    ['colesStoreSizeRank', 'Coles Size Rank'], ['cluster', 'Cluster'],
    ['zoneNumber', 'Zone Number'], ['groupZoneNumber', 'Group/Zone Number'],
    ['storeSize', 'Store Size'], ['tradingArea', 'Trading Area'],
    ['nutBaySize', 'Nut Bay Size'], ['chipsBaySize', 'Chips Bay Size'],
    ['multipacksBaySize', 'Multipacks Bay Size'],
  ]],
  ['WW Programs', [
    ['wwFrontGondola', 'Front Gondola Tie-Up'], ['wwBackGondola', 'Back Gondola Tie-Up'],
    ['wwAcoBin', '3.5HSR ACO Bin'], ['wwEthnicProgram', 'Ethnic Display Program'],
    ['wwEthnicTier', 'Ethnic Store Tier'], ['wwTouristStore', 'Tourist Store'],
    ['wwResortStore', 'Resort Store'], ['wwResortSalesPlan', 'Resort Sales Plan'],
  ]],
  ['Rep Coverage', [
    ['repName', 'Rep'], ['roadRunner', 'Road Runner'],
    ['roadRunnerTerritory', 'Road Runner Territory'], ['repTerritory', 'Rep Territory'],
  ]],
  ['Independent Detail', [
    ['msoGroup', 'MSO Group'], ['macPromoGroup', 'Mac Promo Group'],
    ['macFreightZone', 'Mac Freight Zone'], ['opmetrixCode', 'Opmetrix Code'],
    ['liaiseStoreId', 'Liaise Store ID'], ['liaiseMetcashGrade', 'Liaise Metcash Grade'],
    ['liaiseMso', 'Liaise MSO'], ['liaiseBanner', 'Liaise Banner'],
    ['liaiseWarehouseType', 'Liaise Warehouse Type'], ['liaiseSharedRep', 'Liaise Shared Rep'],
    ['website', 'Website'], ['facebook', 'Facebook'], ['email', 'Email'],
  ]],
];

const EDIT_KEYS = GROUPS.flatMap(([, fields]) => fields.map(([k]) => k));

// Fields offered in the bulk "Set X = Y" picker (safe-to-batch attributes).
const STORE_BULK_FIELDS = [
  ['banner', 'Banner'], ['cluster', 'Cluster'], ['state', 'State'], ['region', 'Region'],
  ['repName', 'Rep'], ['roadRunner', 'Road Runner'], ['storeSize', 'Store Size'],
  ['tradingArea', 'Trading Area'], ['zoneNumber', 'Zone Number'],
].map(([key, label]) => ({ key, label }));
const STORE_LABEL = Object.fromEntries(STORE_BULK_FIELDS.map((f) => [f.key, f.label]));

// Export column order: identity, editable fields, then export-only demographics.
// "Channel" was the legacy internal code (WW/Coles/Independent). Per Matt, the
// export now carries SupplyCustomer (who supplies the store) and Channel = the
// Power BI channel grouping (ChannelGroup) — matching the Field app export.
// Headers must match the import template exactly so a round-trip re-imports
// cleanly: SupplyCustomer (no space), and the rank column keeps its banner-generic
// name (it holds Metcash/Coles ranks alike, despite the DB column being ColesStoreSizeRank).
const EXPORT_HEADER = { supplyCustomer: 'SupplyCustomer', channelGroup: 'Channel', colesStoreSizeRank: 'BannerStoreSizeRank' };

/*
 * Column picker (Matthew, 17 Sep 2026: "the exported data and data when you click on the store
 * is more detailed than shown on the screen ... include a drop down menu which you can tick all
 * the different available attributes to show on the table view").
 *
 * ALL_TABLE_COLS is the pickable set; DEFAULT_TABLE_COLS is exactly the eleven the grid showed
 * before, so the page opens unchanged and everything else is opt-in. A default can be unticked
 * too — the filter applies to the whole set, not just the additions.
 *
 * Stored per user in localStorage, not the DB: a view preference is not a record, and it must
 * never reach the shared store master.
 */
const TABLE_COL_LABEL = {
  storeName: 'Store', storeNumber: 'Number', channel: 'Channel', banner: 'Banner',
  suburb: 'Suburb', state: 'State', postcode: 'Postcode', repName: 'Rep',
  cluster: 'Cluster', isActive: 'Active', updatedAt: 'Updated',
  storeRef: 'Store Ref', customerCode: 'Customer Code', customerName: 'Customer',
  supplyCustomer: 'Supply Customer', channelGroup: 'Channel Group', address: 'Address',
  latitude: 'Latitude', longitude: 'Longitude', region: 'Region', zoneNumber: 'Zone Number',
  roadRunner: 'Road Runner', storeSize: 'Store Size', tradingArea: 'Trading Area',
  colesStoreSizeRank: 'Banner Store Size Rank',
  incomeWeightedAvg: 'Income (weighted avg)', incomePctLow: 'Income % low',
  incomePctMid: 'Income % mid', incomePctHigh: 'Income % high',
  familyPctChildrenU15: 'Families % children <15', familyCountChildrenU15: 'Families, children <15',
  familyCountTotal: 'Families total',
  employmentPctUnemployed: 'Unemployed %', employmentCountUnemployed: 'Unemployed count',
  employmentCountTotal: 'Workforce total',
  ancestryPctSouthAsian: 'South Asian %', ancestryCountSouthAsian: 'South Asian count',
  ancestryCountTotal: 'Ancestry total',
  sa2Name2021: 'SA2', sa3Name2021: 'SA3', sa4Name2021: 'SA4', lgaName2024: 'LGA',
  regionPopulation: 'Region population', sa2Population2023: 'SA2 population',
};
const DEFAULT_TABLE_COLS = [
  'storeName', 'storeNumber', 'channel', 'banner', 'suburb', 'state',
  'postcode', 'repName', 'cluster', 'isActive', 'updatedAt',
];
const ALL_TABLE_COLS = Object.keys(TABLE_COL_LABEL);
const TABLE_COLS_KEY = 'store-master/table-columns/v1';
const EXPORT_KEYS = [
  'storeRef', 'customerCode', 'customerName', 'storeNumber', 'supplyCustomer', 'channelGroup', 'storeName', 'isActive',
  ...EDIT_KEYS,
  'incomePctLow', 'incomePctMid', 'incomePctHigh', 'incomeWeightedAvg',
  'familyPctChildrenU15', 'familyCountChildrenU15', 'familyCountTotal',
  'employmentPctUnemployed', 'employmentCountUnemployed', 'employmentCountTotal',
  'ancestryPctSouthAsian', 'ancestryCountSouthAsian', 'ancestryCountTotal',
  'sa2Name2021', 'sa3Name2021', 'sa4Name2021', 'lgaName2024',
  'regionPopulation', 'sa2Population2023', 'updatedAt',
];

// Import header → API key. Accepts our own export headers (camelCase) and the
// FIELDMASTER originals (incl. the "Tourims Regions" typo and the double-space
// "Latitude  [ Store_1 ]"). Normalised: lowercase, non-alphanumerics stripped.
const IMPORT_ALIASES = {
  customeraccount: 'customerCode',
  // SupplyCustomer (who supplies the store) drives the internal channel server-
  // side (deriveChannel: Costco/ALDI→OTHER etc.), so new stores resolve a valid
  // channel. The file's "Channel" column is Matt's PB grouping → channelGroup,
  // NOT the internal WW/Coles/Independent code (mapping it there rejected new
  // Costco/ALDI rows — "added 0, errors". The internal channel is derived).
  supplycustomer: 'supplyCustomer',
  channel: 'channelGroup',
  channelgroup: 'channelGroup',
  store: 'storeName',
  storephonenumbers: 'phone',
  tourimsregions: 'tourismRegion',
  latitudestore1: 'latitude',
  longitudestore1: 'longitude',
  colesstoresizeranking: 'colesStoreSizeRank',
  bannerstoresizerank: 'colesStoreSizeRank',   // export/import header; DB col is ColesStoreSizeRank but holds Metcash ranks too
  customerstorecluster: 'cluster',
  customerzonenumber: 'zoneNumber',
  customergroupzonenumber: 'groupZoneNumber',
  nutbaysize: 'nutBaySize',
  chipssharingbaysize: 'chipsBaySize',
  multipacksbaysize: 'multipacksBaySize',
  wwfrontgondolaendtieup: 'wwFrontGondola',
  wwbackgondolaendtieup: 'wwBackGondola',
  ww35hsracobin: 'wwAcoBin',
  wwethnicdisplayprogram: 'wwEthnicProgram',
  wwethnicstoretiers: 'wwEthnicTier',
  independentmsogroup: 'msoGroup',
  actualstorenamecustomerfacing: 'customerFacingName',
  maccustomerpromogroupname: 'macPromoGroup',
  maczonefreightpricing: 'macFreightZone',
  liaiseopmetrixcode: 'opmetrixCode',
  liaisemarketingrepname: 'repName',
  roadrunnername: 'roadRunner',
  sharedrepterritory: 'repTerritory',
};
const KNOWN_KEYS = new Set(['storeRef', 'customerCode', 'storeNumber', 'channel', 'storeName', 'isActive', ...EDIT_KEYS]);

const normHeader = (h) => String(h ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const KEY_BY_NORM = Object.fromEntries([...KNOWN_KEYS].map((k) => [normHeader(k), k]));

function mapImportRow(raw) {
  const out = {};
  let inactiveFlag;
  for (const [header, value] of Object.entries(raw)) {
    const n = normHeader(header);
    if (n === 'storeinactive') { inactiveFlag = value; continue; }   // FIELDMASTER inverted flag
    const key = IMPORT_ALIASES[n] ?? KEY_BY_NORM[n];
    if (key && value !== undefined && value !== '') out[key] = value;
  }
  if (inactiveFlag !== undefined && out.isActive === undefined) {
    out.isActive = !['Y', 'YES'].includes(String(inactiveFlag).trim().toUpperCase());
  }
  return out;
}

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const CHIP = { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, border: '1px solid var(--border-soft)', color: 'var(--text-muted)' };
const LINK_BTN = { background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 };

export default function StoreMaster() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const fileRef = useRef(null);

  const { data: stores = [], isLoading, error } = useQuery({
    queryKey: ['store-master'],
    queryFn: () => apiFetch('/stores').then((r) => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); }),
    staleTime: 5 * 60 * 1000,
  });

  const [view, setView] = useState('stores');          // 'stores' | 'matrix'
  const [channel, setChannel] = useState('All');
  const [banner, setBanner] = useState('All');
  const [state, setState] = useState('All');
  const [active, setActive] = useState('Active');
  const [q, setQ] = useState('');
  const [editor, setEditor] = useState(null);          // {store, isNew}
  const [importState, setImportState] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [gridError, setGridError] = useState(null);

  const [storeApi, setStoreApi] = useState(null);
  const [storeSelected, setStoreSelected] = useState(0);
  const [bulk, setBulk] = useState(null);              // store bulk-edit flow

  const banners = useMemo(() => {
    const pool = channel === 'All' ? stores : stores.filter((s) => s.channel === channel);
    return ['All', ...Array.from(new Set(pool.map((s) => s.banner).filter(Boolean))).sort()];
  }, [stores, channel]);
  const states = useMemo(() => ['All', ...Array.from(new Set(stores.map((s) => s.state).filter(Boolean))).sort()], [stores]);

  // Column picker. Every read and write is wrapped: localStorage throws in private mode, and
  // a view preference must never be the reason the page fails to render.
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(TABLE_COLS_KEY) ?? 'null');
      if (Array.isArray(saved) && saved.length) return saved.filter((k) => k in TABLE_COL_LABEL);
    } catch { /* fall through to the default view */ }
    return DEFAULT_TABLE_COLS;
  });
  const [colPickerOpen, setColPickerOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem(TABLE_COLS_KEY, JSON.stringify(visibleCols)); } catch { /* ignore */ }
  }, [visibleCols]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return stores.filter((s) =>
      (channel === 'All' || s.channel === channel) &&
      (banner === 'All' || s.banner === banner) &&
      (state === 'All' || s.state === state) &&
      (active === 'All' || (active === 'Active' ? s.isActive : !s.isActive)) &&
      (!needle || [s.storeName, s.customerFacingName, s.storeNumber, s.suburb, s.postcode, s.repName]
        .some((v) => v && String(v).toLowerCase().includes(needle))),
    );
  }, [stores, channel, banner, state, active, q]);

  const counts = useMemo(() => {
    const by = { WW: [0, 0], COLES: [0, 0], INDEPENDENT: [0, 0] };
    for (const s of stores) {
      if (!by[s.channel]) continue;
      by[s.channel][0] += 1;
      if (s.isActive) by[s.channel][1] += 1;
    }
    return by;
  }, [stores]);

  const setFilter = (fn) => (v) => { fn(v); };

  const saveMutation = useMutation({
    mutationFn: async ({ ref, body }) => {
      const res = await apiFetch(`/stores/${encodeURIComponent(ref)}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Save failed (${res.status})`);
      return res.json();
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['store-master'] }); setEditor(null); },
  });

  /* ── Stores grid ─────────────────────────────────────────────────────── */

  const storeColumns = useMemo(() => [
    {
      headerName: '', field: '__open', pinned: 'left', width: 64, sortable: false, filter: false,
      editable: false, resizable: false, suppressMovable: true,
      cellRenderer: (p) => (p.data
        ? <button style={LINK_BTN} onClick={() => setEditor({ isNew: false, store: p.data })}>Open</button>
        : null),
    },
    { headerName: 'Store', field: 'storeName', pinned: 'left', flex: 1, minWidth: 200, editable: true },
    { headerName: 'Number', field: 'storeNumber', width: 110, editable: false },
    { headerName: 'Channel', field: 'channel', width: 120, editable: false },
    { headerName: 'Banner', field: 'banner', width: 150, editable: true },
    { headerName: 'Suburb', field: 'suburb', width: 140, editable: true },
    { headerName: 'State', field: 'state', width: 90, editable: true },
    { headerName: 'Postcode', field: 'postcode', width: 100, editable: true },
    { headerName: 'Rep', field: 'repName', width: 150, editable: true },
    { headerName: 'Cluster', field: 'cluster', width: 120, editable: true },
    { headerName: 'Active', field: 'isActive', width: 90, editable: true, cellDataType: 'boolean' },
    { headerName: 'Updated', field: 'updatedAt', width: 150, editable: false, filter: false, valueFormatter: (p) => fmtDate(p.value) },
    // Everything the record carries that the default view does not show. Read-only on purpose:
    // the demographics come from the ingest, not from typing, and the identity fields are the
    // store's key — both are edited (where editable at all) in the detail drawer.
    ...ALL_TABLE_COLS
      .filter((k) => !DEFAULT_TABLE_COLS.includes(k))
      .map((k) => ({ headerName: TABLE_COL_LABEL[k], field: k, width: 150, editable: false })),
    // One filter over the whole set, so a DEFAULT column can be unticked too — not just the
    // additions. The Open button has no field and always stays.
  ].filter((c) => !c.field || c.field === '__open' || visibleCols.includes(c.field)),
  [visibleCols]);

  const onStoreCellChanged = useCallback(async (e) => {
    const field = e.colDef.field;
    if (!field || e.newValue === e.oldValue) return;
    try {
      const res = await apiFetch(`/stores/${encodeURIComponent(e.data.storeRef)}`, { method: 'PUT', body: JSON.stringify({ [field]: e.newValue }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Save failed (${res.status})`);
      setGridError(null);
      qc.invalidateQueries({ queryKey: ['store-master'] });
    } catch (err) {
      setGridError(`${e.data.storeName}: ${String(err.message ?? err)}`);
      e.node.setDataValue(field, e.oldValue);
    }
  }, [apiFetch, qc]);

  async function onStoreBulkApply(field, value) {
    const rows = storeApi?.getSelectedRows() ?? [];
    if (!rows.length) return;
    const importRows = rows.map((r) => ({ storeRef: r.storeRef, [field]: value }));
    setBulk({ field, value, rows: importRows, applying: false });
    try {
      const res = await apiFetch('/stores/import', { method: 'POST', body: JSON.stringify({ rows: importRows, dryRun: true, fileName: `bulk:${field}` }) });
      const preview = await res.json();
      if (!res.ok) throw new Error(preview?.error ?? `Preview failed (${res.status})`);
      setBulk((s) => ({ ...s, lines: [
        `${preview.changes.length} store(s) will be updated.`,
        `Set ${STORE_LABEL[field] ?? field} = ${value === '' ? '(blank)' : value}.`,
        ...(preview.errors.length ? [`${preview.errors.length} row(s) skipped.`] : []),
      ] }));
    } catch (err) { setBulk((s) => ({ ...s, error: String(err.message ?? err) })); }
  }

  async function commitStoreBulk() {
    setBulk((s) => ({ ...s, applying: true }));
    try {
      const res = await apiFetch('/stores/import', { method: 'POST', body: JSON.stringify({ rows: bulk.rows, dryRun: false, fileName: `bulk:${bulk.field}` }) });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.error ?? `Apply failed (${res.status})`);
      qc.invalidateQueries({ queryKey: ['store-master'] });
      storeApi?.deselectAll();
      setStoreSelected(0);
      setBulk((s) => ({ ...s, applying: false, done: `Done — ${result.changes.length} updated, ${result.errors.length} errors.` }));
    } catch (err) { setBulk((s) => ({ ...s, applying: false, error: String(err.message ?? err) })); }
  }

  /* ── Ranging matrix grid ─────────────────────────────────────────────── */

  const { data: matrixData, isLoading: matrixLoading, error: matrixError } = useQuery({
    queryKey: ['ranging-matrix'],
    queryFn: () => apiFetch('/ranging/matrix').then((r) => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); }),
    enabled: view === 'matrix',
    staleTime: 60_000,
  });
  const matrixItems = useMemo(() => matrixData?.items ?? [], [matrixData]);
  const itemByNum = useMemo(() => Object.fromEntries(matrixItems.map((it) => [it.itemNumber, it])), [matrixItems]);
  const matrixRows = useMemo(() => (matrixData?.rows ?? []).map((s) => {
    const o = { storeRef: s.storeRef, storeName: s.storeName, banner: s.banner, state: s.state, customerCode: s.customerCode };
    matrixItems.forEach((it, i) => { o[it.itemNumber] = !!(s.ranged?.[i]); });
    return o;
  }), [matrixData, matrixItems]);
  const matrixColumns = useMemo(() => [
    { headerName: 'Store', field: 'storeName', pinned: 'left', width: 210, editable: false },
    { headerName: 'Banner', field: 'banner', pinned: 'left', width: 120, editable: false },
    { headerName: 'State', field: 'state', pinned: 'left', width: 80, editable: false },
    // Matthew, 17 Sep: at width 72 a product name showed as five letters and an ellipsis, so
    // you could not tell the SKUs apart. The header now WRAPS over three lines and the grid
    // gives it the height to do it (wrapHeaderText + autoHeaderHeight on the grid below);
    // 118px fits "Infuzions Air Fries Peri Peri 6 x 90g" across three lines while still
    // showing a useful number of columns at once.
    ...matrixItems.map((it) => ({
      headerName: it.productName ?? it.ean ?? it.itemNumber,
      field: it.itemNumber, width: 118, editable: true, cellDataType: 'boolean', filter: false, sortable: false,
      wrapHeaderText: true, autoHeaderHeight: true,
      headerTooltip: `${it.productName ?? ''}${it.ean ? ' · ' + it.ean : ''} (#${it.itemNumber})`.trim(),
    })),
  ], [matrixItems]);
  const matrixBulkFields = useMemo(
    () => matrixItems.map((it) => ({ key: it.itemNumber, label: it.productName ?? it.ean ?? it.itemNumber })), [matrixItems]);

  /*
   * Delete a store (Matthew, 17 Sep). Confirmed twice over: the drawer asks, and the server
   * refuses nothing — so the confirm IS the gate. Safe to be a hard delete because
   * commercial.Store is system-versioned: the row and its history stay readable with
   * FOR SYSTEM_TIME. The store's own children (ranging, demographics, the two extension
   * tables, liaise) go with it server-side, in one transaction.
   */
  const deleteStoreMutation = useMutation({
    mutationFn: async (ref) => {
      const res = await apiFetch(`/stores/${encodeURIComponent(ref)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Delete failed (${res.status})`);
      return res.json();
    },
    onSuccess: () => {
      setEditor(null);
      // Both lists: a deleted store must leave the ranging matrix in the same breath, or the
      // matrix shows a row that no longer exists until someone reloads.
      qc.invalidateQueries({ queryKey: ['store-master'] });
      qc.invalidateQueries({ queryKey: ['ranging-matrix'] });
    },
  });

  /* Add a SKU column to the matrix (migration 068). See the API for why the old scope was
   * circular — a new listing could never be prepared before distribution was agreed. */
  const [addSkuOpen, setAddSkuOpen] = useState(false);
  const addSkuMutation = useMutation({
    mutationFn: async (itemNumber) => {
      const res = await apiFetch('/ranging/items', { method: 'POST', body: JSON.stringify({ itemNumber }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Add failed (${res.status})`);
      return res.json();
    },
    onSuccess: () => {
      setAddSkuOpen(false);
      // ranging-matrix carries the item list too, so this refreshes the new column;
      // ranging-items-available is the picker, which must stop offering what we just added.
      qc.invalidateQueries({ queryKey: ['ranging-matrix'] });
      qc.invalidateQueries({ queryKey: ['ranging-items-available'] });
    },
  });

  const [matrixApi, setMatrixApi] = useState(null);
  const [matrixSelected, setMatrixSelected] = useState(0);
  const [matrixBulk, setMatrixBulk] = useState(null);

  const onMatrixCellChanged = useCallback(async (e) => {
    const itemNumber = e.colDef.field;
    if (!itemNumber || e.newValue === e.oldValue) return;
    try {
      const res = await apiFetch('/ranging', { method: 'PUT', body: JSON.stringify({ storeRef: e.data.storeRef, itemNumber, ranged: !!e.newValue }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Toggle failed (${res.status})`);
      setGridError(null);
      /*
       * Matthew, 17 Sep: "ensure if the ranging matrix is changed that it is reflected on the
       * stores as well". The stores view is a SEPARATE query (['store-master']) with a 60s
       * stale time, so without this it kept serving the copy it loaded before the toggle and
       * the change looked like it had not taken until a reload.
       *
       * ⚠ The key must be ['store-master'] — the name the query actually uses. An invalidate
       * on a key nothing is registered under does not error, it just silently does nothing.
       */
      qc.invalidateQueries({ queryKey: ['store-master'] });
    } catch (err) {
      setGridError(`${e.data.storeName}: ${String(err.message ?? err)}`);
      e.node.setDataValue(itemNumber, e.oldValue);
    }
  }, [apiFetch]);

  async function onMatrixBulkApply(itemNumber, value) {
    const selStores = matrixApi?.getSelectedRows() ?? [];
    if (!selStores.length) return;
    const ranged = value === '1';
    const item = itemByNum[itemNumber];
    const label = item?.productName ?? item?.ean ?? itemNumber;
    if (item?.ean) {
      // One governed import request (dry-run → apply), single audit row.
      const rows = selStores.map((s) => ({ storeRef: s.storeRef, flags: { [item.ean]: ranged ? 1 : 0 } }));
      setMatrixBulk({ mode: 'import', rows, itemNumber, ranged, label, applying: false });
      try {
        const res = await apiFetch('/ranging/import', { method: 'POST', body: JSON.stringify({ rows, dryRun: true, fileName: `bulk:${itemNumber}` }) });
        const preview = await res.json();
        if (!res.ok) throw new Error(preview?.error ?? `Preview failed (${res.status})`);
        setMatrixBulk((s) => ({ ...s, lines: [
          `${preview.opens} store(s) to range, ${preview.closes} to de-range.`,
          `SKU: ${label} → ${ranged ? 'Ranged' : 'De-ranged'}.`,
        ] }));
      } catch (err) { setMatrixBulk((s) => ({ ...s, error: String(err.message ?? err) })); }
    } else {
      // No EAN to address the SKU by — fall back to per-cell PUT (still audited).
      setMatrixBulk({ mode: 'put', stores: selStores, itemNumber, ranged, label, applying: false, lines: [
        `${selStores.length} store(s) will be set to ${ranged ? 'Ranged' : 'De-ranged'}.`,
        `SKU: ${label} (no EAN — applied per store).`,
      ] });
    }
  }

  async function commitMatrixBulk() {
    setMatrixBulk((s) => ({ ...s, applying: true }));
    try {
      const b = matrixBulk;
      if (b.mode === 'import') {
        const res = await apiFetch('/ranging/import', { method: 'POST', body: JSON.stringify({ rows: b.rows, dryRun: false, fileName: `bulk:${b.itemNumber}` }) });
        const result = await res.json();
        if (!res.ok) throw new Error(result?.error ?? `Apply failed (${res.status})`);
        setMatrixBulk((s) => ({ ...s, applying: false, done: `Done — ${result.opens} ranged, ${result.closes} de-ranged.` }));
      } else {
        for (const s of b.stores) {
          const res = await apiFetch('/ranging', { method: 'PUT', body: JSON.stringify({ storeRef: s.storeRef, itemNumber: b.itemNumber, ranged: b.ranged }) });
          if (!res.ok) throw new Error(`Toggle failed for ${s.storeName ?? s.storeRef}`);
        }
        setMatrixBulk((s) => ({ ...s, applying: false, done: `Done — ${b.stores.length} store(s) updated.` }));
      }
      qc.invalidateQueries({ queryKey: ['ranging-matrix'] });
      matrixApi?.deselectAll();
      setMatrixSelected(0);
    } catch (err) { setMatrixBulk((s) => ({ ...s, applying: false, error: String(err.message ?? err) })); }
  }

  /* ── Excel round-trip (stores) ───────────────────────────────────────── */

  async function exportExcel() {
    setExporting(true);
    try {
      // Demographics are export-only — fetch the full column set on demand
      // (the page's working query stays lean), then apply the current filters.
      const XLSX = await import('xlsx');
      const res = await apiFetch('/stores?demo=true');
      if (!res.ok) throw new Error(`Export fetch failed (${res.status})`);
      const all = await res.json();
      const keep = new Set(filtered.map((s) => s.storeRef));
      const rows = all.filter((s) => keep.has(s.storeRef))
        .map((s) => Object.fromEntries(EXPORT_KEYS.map((k) => [EXPORT_HEADER[k] ?? k, k === 'isActive' ? (s[k] ? 1 : 0) : s[k] ?? ''])));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Stores');
      XLSX.writeFile(wb, `StoreMaster_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setExporting(false);
    }
  }

  async function onImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer());
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });
    const rows = raw.map(mapImportRow).filter((r) => r.storeRef || (r.customerCode && r.storeNumber));
    if (!rows.length) {
      setImportState({ fileName: file.name, rows: [], preview: null, error: 'No usable rows found — the first sheet needs storeRef (or customerCode + storeNumber) columns.' });
      return;
    }
    setImportState({ fileName: file.name, rows, preview: null, committing: false, deactivateMissing: false });
    try {
      const res = await apiFetch('/stores/import', { method: 'POST', body: JSON.stringify({ rows, dryRun: true, fileName: file.name }) });
      const preview = await res.json();
      if (!res.ok) throw new Error(preview?.error ?? `Import preview failed (${res.status})`);
      setImportState((s) => ({ ...s, preview }));
    } catch (err) {
      setImportState((s) => ({ ...s, error: String(err.message ?? err) }));
    }
  }

  async function commitImport() {
    setImportState((s) => ({ ...s, committing: true }));
    try {
      const { rows, fileName, deactivateMissing } = importState;
      const res = await apiFetch('/stores/import', {
        method: 'POST',
        body: JSON.stringify({ rows, dryRun: false, deactivateMissing, fileName }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.error ?? `Import failed (${res.status})`);
      qc.invalidateQueries({ queryKey: ['store-master'] });
      setImportState((s) => ({ ...s, committing: false, result }));
    } catch (err) {
      setImportState((s) => ({ ...s, committing: false, error: String(err.message ?? err) }));
    }
  }

  /* ── Excel round-trip (ranging matrix) ───────────────────────────────── */

  const rangingFileRef = useRef(null);
  const [rangingImport, setRangingImport] = useState(null);

  async function exportRangingMatrix() {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const res = await apiFetch('/ranging/matrix');
      if (!res.ok) throw new Error(`Ranging fetch failed (${res.status})`);
      const { items, rows } = await res.json();
      const idCols = ['customerCode', 'storeNumber', 'storeName', 'banner', 'state'];
      const itemKeys = items.map((it) => String(it.ean ?? it.itemNumber));
      const sheetRows = rows.map((r) => {
        const o = { customerCode: r.customerCode, storeNumber: r.storeNumber, storeName: r.storeName, banner: r.banner, state: r.state };
        items.forEach((it, i) => { o[String(it.ean ?? it.itemNumber)] = r.ranged[i]; });
        return o;
      });
      const wb = XLSX.utils.book_new();
      /*
       * Matthew, 17 Sep: "it only includes a barcode number for a SKU at the top of the column.
       * Can you ensure there is a row above this which also includes the item name."
       *
       * So row 1 is the product names and row 2 is the barcodes. The BARCODE row stays the
       * header row because that is what the importer keys on — onRangingFile finds the row
       * whose cells are 8+ digits rather than assuming row 1, so this round-trips. Put the
       * names in row 2 instead and the file would re-import as a sheet of zero SKUs.
       */
      const nameRow = [...idCols.map(() => ''), ...items.map((it) => it.productName ?? '')];
      const sheet = XLSX.utils.aoa_to_sheet([nameRow]);
      XLSX.utils.sheet_add_json(sheet, sheetRows, { origin: 'A2', header: [...idCols, ...itemKeys] });
      XLSX.utils.book_append_sheet(wb, sheet, 'Ranging');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(items), 'Items');
      XLSX.writeFile(wb, `Ranging_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setExporting(false);
    }
  }

  async function onRangingFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer());
    const sheet = wb.Sheets[wb.SheetNames[0]];
    /*
     * Find the header row rather than assuming row 1. Since the export gained a product-name
     * row above the barcodes (Matthew, 17 Sep), the EANs sit on row 2 of our own file — while
     * a FIELDMASTER sheet still has them on row 1. The header row is the first row carrying
     * two or more 8+ digit cells. Getting this wrong does not error: it silently imports a
     * sheet with zero SKU columns and reports "no changes".
     */
    const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const looksLikeEan = (v) => /^\d{8,}$/.test(String(v ?? '').trim());
    let headerRow = grid.findIndex((row) => Array.isArray(row) && row.filter(looksLikeEan).length >= 2);
    if (headerRow < 0) headerRow = 0;
    const raw = XLSX.utils.sheet_to_json(sheet, { defval: '', range: headerRow });
    // EAN columns = headers that are all digits (8+). Works for our export and
    // for a FIELDMASTER-style sheet saved with EAN headers.
    const rows = raw.map((r) => {
      const flags = {};
      let customerCode, storeNumber, storeRef;
      for (const [h, v] of Object.entries(r)) {
        const n = normHeader(h);
        if (/^\d{8,}$/.test(String(h).trim())) flags[String(h).trim()] = Number(v) === 1 ? 1 : 0;
        else if (n === 'customercode' || n === 'customeraccount') customerCode = v;
        else if (n === 'storenumber') storeNumber = v;
        else if (n === 'storeref') storeRef = v;
      }
      return { storeRef: storeRef ? String(storeRef).trim() : undefined, customerCode: customerCode ? String(customerCode).trim() : undefined, storeNumber: storeNumber !== '' && storeNumber !== undefined ? String(storeNumber).trim() : undefined, flags };
    }).filter((r) => (r.storeRef || (r.customerCode && r.storeNumber)) && Object.keys(r.flags).length);
    if (!rows.length) {
      setRangingImport({ fileName: file.name, error: 'No usable rows — need customerCode+storeNumber (or storeRef) plus EAN-numbered columns.' });
      return;
    }
    setRangingImport({ fileName: file.name, rows, replace: false, committing: false });
    await previewRanging(rows, file.name, false);
  }

  async function previewRanging(rows, fileName, replace) {
    try {
      const res = await apiFetch('/ranging/import', { method: 'POST', body: JSON.stringify({ rows, dryRun: true, fileName, replace }) });
      const preview = await res.json();
      if (!res.ok) throw new Error(preview?.error ?? `Preview failed (${res.status})`);
      setRangingImport((s) => ({ ...s, preview, error: undefined }));
    } catch (err) {
      setRangingImport((s) => ({ ...s, error: String(err.message ?? err) }));
    }
  }

  async function toggleRangingReplace(replace) {
    setRangingImport((s) => ({ ...s, replace, preview: null }));
    await previewRanging(rangingImport.rows, rangingImport.fileName, replace);
  }

  async function commitRangingImport() {
    setRangingImport((s) => ({ ...s, committing: true }));
    try {
      const { rows, fileName, replace } = rangingImport;
      const res = await apiFetch('/ranging/import', { method: 'POST', body: JSON.stringify({ rows, dryRun: false, fileName, replace }) });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.error ?? `Import failed (${res.status})`);
      qc.invalidateQueries({ queryKey: ['ranging-matrix'] });
      setRangingImport((s) => ({ ...s, committing: false, result }));
    } catch (err) {
      setRangingImport((s) => ({ ...s, committing: false, error: String(err.message ?? err) }));
    }
  }

  // Clear any cross-view error when switching views.
  useEffect(() => { setGridError(null); }, [view]);

  if (error) {
    return <div style={{ padding: 24, color: 'var(--danger)' }}>Failed to load stores: {String(error.message ?? error)}</div>;
  }

  const TabBtn = ({ id, label }) => (
    <button
      onClick={() => setView(id)}
      style={{
        padding: '5px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 'var(--radius)',
        border: '1px solid ' + (view === id ? 'var(--accent)' : 'var(--border-soft)'),
        background: view === id ? 'var(--accent-soft)' : 'transparent',
        color: view === id ? 'var(--text-primary)' : 'var(--text-muted)',
      }}
    >{label}</button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* View toggle + summary + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <TabBtn id="stores" label="Stores" />
        <TabBtn id="matrix" label="Ranging Matrix" />
        <span style={{ width: 12 }} />
        {CHANNELS.map((ch) => (
          <span key={ch} style={CHIP}>
            {ch} {counts[ch][1].toLocaleString()} active / {counts[ch][0].toLocaleString()}
          </span>
        ))}
        <span style={{ flex: 1 }} />
        {view === 'stores' ? (
          <>
            <div style={{ position: 'relative' }}>
              <Button size="sm" onClick={() => setColPickerOpen((o) => !o)}>
                Columns ({visibleCols.length})
              </Button>
              {colPickerOpen && (
                <>
                  {/* Click-away. A plain overlay rather than a document listener: the listener
                      version closed the panel on the very click that opened it. */}
                  <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setColPickerOpen(false)} />
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 41,
                    width: 280, maxHeight: 360, overflowY: 'auto', background: 'var(--bg-card)',
                    border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 10 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <button style={LINK_BTN} onClick={() => setVisibleCols(ALL_TABLE_COLS)}>All</button>
                      <button style={LINK_BTN} onClick={() => setVisibleCols(DEFAULT_TABLE_COLS)}>Default</button>
                    </div>
                    {ALL_TABLE_COLS.map((k) => (
                      <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0',
                        fontSize: 12, color: 'var(--text-body)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={visibleCols.includes(k)}
                          onChange={() => setVisibleCols((cur) =>
                            cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k])} />
                        {TABLE_COL_LABEL[k]}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <Button size="sm" onClick={() => setEditor({ isNew: true, store: { channel: 'INDEPENDENT', isActive: true } })}>+ New Store</Button>
            <Button size="sm" onClick={() => fileRef.current?.click()}>Import Excel</Button>
            <Button size="sm" variant="primary" disabled={exporting || isLoading} onClick={exportExcel}>
              {exporting ? 'Exporting…' : `Export Excel (${filtered.length.toLocaleString()})`}
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" onClick={() => setAddSkuOpen(true)}>+ Add SKU</Button>
            <Button size="sm" onClick={() => rangingFileRef.current?.click()}>Import ranging</Button>
            <Button size="sm" disabled={exporting} onClick={exportRangingMatrix}>Export ranging</Button>
          </>
        )}
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={onImportFile} />
        <input ref={rangingFileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onRangingFile} />
      </div>

      {view === 'stores' && (
        <>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ width: 150 }}>
              <Label>Channel</Label>
              <Select value={channel} onChange={(e) => { setFilter(setChannel)(e.target.value); setBanner('All'); }}>
                {['All', ...CHANNELS].map((c) => <option key={c}>{c}</option>)}
              </Select>
            </div>
            <div style={{ width: 190 }}>
              <Label>Banner</Label>
              <Select value={banner} onChange={(e) => setFilter(setBanner)(e.target.value)}>
                {banners.map((b) => <option key={b}>{b}</option>)}
              </Select>
            </div>
            <div style={{ width: 110 }}>
              <Label>State</Label>
              <Select value={state} onChange={(e) => setFilter(setState)(e.target.value)}>
                {states.map((s) => <option key={s}>{s}</option>)}
              </Select>
            </div>
            <div style={{ width: 120 }}>
              <Label>Status</Label>
              <Select value={active} onChange={(e) => setFilter(setActive)(e.target.value)}>
                {['Active', 'Inactive', 'All'].map((a) => <option key={a}>{a}</option>)}
              </Select>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Label>Search</Label>
              <Input placeholder="Store name, number, suburb, postcode, rep…" value={q} onChange={(e) => setFilter(setQ)(e.target.value)} />
            </div>
          </div>

          <BulkEditBar
            selectedCount={storeSelected}
            fields={STORE_BULK_FIELDS}
            noun="stores"
            applying={!!bulk && !bulk.done && !bulk.error}
            onApply={onStoreBulkApply}
            onClear={() => { storeApi?.deselectAll(); setStoreSelected(0); }}
          />

          {gridError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{gridError}</div>}

          <DataGrid
            rowData={filtered}
            columnDefs={storeColumns}
            onGridReady={(e) => setStoreApi(e.api)}
            onCellValueChanged={onStoreCellChanged}
            onSelectionChanged={(e) => setStoreSelected(e.api.getSelectedRows().length)}
            rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true, selectAll: 'filtered', enableClickSelection: false }}
            getRowId={(p) => String(p.data.storeRef)}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'var(--text-muted)' }}>
            <span>{filtered.length.toLocaleString()} stores{storeSelected ? ` · ${storeSelected.toLocaleString()} selected` : ''}</span>
            <span style={{ flex: 1 }} />
            <span>Double-click a cell to edit · tick rows to bulk-edit · Open for the full record</span>
          </div>
        </>
      )}

      {view === 'matrix' && (
        <>
          <BulkEditBar
            selectedCount={matrixSelected}
            fields={matrixBulkFields}
            valueOptions={[{ value: '1', label: 'Ranged' }, { value: '0', label: 'De-ranged' }]}
            noun="stores"
            applying={!!matrixBulk && !matrixBulk.done && !matrixBulk.error}
            onApply={onMatrixBulkApply}
            onClear={() => { matrixApi?.deselectAll(); setMatrixSelected(0); }}
          />

          {gridError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{gridError}</div>}
          {matrixError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>Failed to load ranging matrix: {String(matrixError.message ?? matrixError)}</div>}

          {matrixLoading
            ? <div style={{ padding: 16, fontSize: 13, color: 'var(--text-muted)' }}>Loading ranging matrix…</div>
            : (
              <DataGrid
                rowData={matrixRows}
                columnDefs={matrixColumns}
                onGridReady={(e) => setMatrixApi(e.api)}
                onCellValueChanged={onMatrixCellChanged}
                onSelectionChanged={(e) => setMatrixSelected(e.api.getSelectedRows().length)}
                rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true, selectAll: 'filtered', enableClickSelection: false }}
                getRowId={(p) => String(p.data.storeRef)}
              />
            )}

          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {matrixRows.length.toLocaleString()} WW/Coles stores × {matrixItems.length.toLocaleString()} SKUs · click a cell to range/de-range · tick rows to bulk a SKU
          </div>
        </>
      )}

      {editor && (
        <EditDrawer
          editor={editor}
          onClose={() => setEditor(null)}
          onSave={(ref, body) => saveMutation.mutate({ ref, body })}
          saving={saveMutation.isPending}
          saveError={saveMutation.error}
          onDelete={(ref) => deleteStoreMutation.mutate(ref)}
          deleting={deleteStoreMutation.isPending}
          deleteError={deleteStoreMutation.error}
        />
      )}
      {importState && (
        <ImportModal
          state={importState}
          onToggleDeactivate={(v) => setImportState((s) => ({ ...s, deactivateMissing: v }))}
          onCommit={commitImport}
          onClose={() => setImportState(null)}
        />
      )}
      {addSkuOpen && (
        <AddSkuModal
          apiFetch={apiFetch}
          onClose={() => setAddSkuOpen(false)}
          onAdd={(itemNumber) => addSkuMutation.mutate(itemNumber)}
          adding={addSkuMutation.isPending}
          error={addSkuMutation.error}
        />
      )}
      {rangingImport && (
        <RangingImportModal
          state={rangingImport}
          onCommit={commitRangingImport}
          onToggleReplace={toggleRangingReplace}
          onClose={() => setRangingImport(null)}
        />
      )}
      {bulk && <BulkPreviewModal title="Bulk update stores" lines={bulk.lines} error={bulk.error}
        applying={bulk.applying} done={bulk.done} onApply={commitStoreBulk} onClose={() => setBulk(null)} />}
      {matrixBulk && <BulkPreviewModal title="Bulk update ranging" lines={matrixBulk.lines} error={matrixBulk.error}
        applying={matrixBulk.applying} done={matrixBulk.done} onApply={commitMatrixBulk} onClose={() => setMatrixBulk(null)} />}
    </div>
  );
}

/* ── Edit drawer ─────────────────────────────────────────────────────────── */

/*
 * Add a SKU column to the store ranging matrix (migration 068).
 *
 * Only offers items that exist in commercial.Item, which is synced from the lakehouse — a SKU
 * that is not in D365 yet cannot be added, and the empty state says so rather than leaving the
 * user hunting. Adding a column writes NO ranging: a new listing starts ranged nowhere, which
 * is the honest state before distribution is agreed.
 */
function AddSkuModal({ apiFetch, onClose, onAdd, adding, error }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const { data: available, isLoading } = useQuery({
    queryKey: ['ranging-items-available', q],
    queryFn: () => apiFetch(`/ranging/items/available${q ? `?q=${encodeURIComponent(q)}` : ''}`)
      .then((r) => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); }),
    staleTime: 30_000,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
      <div style={{ position: 'absolute', top: '10vh', left: '50%', transform: 'translateX(-50%)',
        width: 'min(560px, 96vw)', maxHeight: '76vh', display: 'flex', flexDirection: 'column',
        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        boxShadow: '0 12px 32px rgba(0,0,0,0.22)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Add a SKU to the matrix</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            It starts ranged nowhere — tick the stores afterwards.
          </div>
        </div>
        <div style={{ padding: '10px 18px' }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, barcode or item code…" />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 18px 10px' }}>
          {isLoading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
          {!isLoading && (available ?? []).length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Nothing matches. Items come from D365 via the weekly sync, so a brand-new SKU has to
              exist there first — then it appears here.
            </div>
          )}
          {(available ?? []).map((it) => (
            <label key={it.itemNumber} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
              borderBottom: '1px solid var(--border-soft)', fontSize: 12, cursor: 'pointer' }}>
              <input type="radio" name="sku" checked={picked === it.itemNumber} onChange={() => setPicked(it.itemNumber)} />
              <span style={{ flex: 1, color: 'var(--text-primary)' }}>{it.productName ?? '(no name)'}</span>
              <span style={{ color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>
                {it.ean ?? '—'} · #{it.itemNumber}
              </span>
            </label>
          ))}
        </div>
        {error && (
          <div style={{ padding: '0 18px 8px', fontSize: 11, color: 'var(--danger)' }}>
            {String(error.message ?? error)}
          </div>
        )}
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="primary" disabled={!picked || adding} onClick={() => onAdd(picked)}>
            {adding ? 'Adding…' : 'Add to matrix'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditDrawer({ editor, onClose, onSave, saving, saveError, onDelete, deleting, deleteError }) {
  const { isNew } = editor;
  // Two-step delete, in the panel rather than a window.confirm: Matthew asked for a
  // confirmation, and an inline one can say WHAT goes with the store instead of just "OK?".
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState(() => ({ ...editor.store }));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ref = isNew ? `${form.customerCode ?? ''}${form.storeNumber ?? ''}` : form.storeRef;
  const canSave = isNew
    ? form.customerCode && form.storeNumber && form.channel && form.storeName
    : true;

  function submit() {
    const body = {};
    for (const k of EDIT_KEYS) if (form[k] !== undefined) body[k] = form[k] === '' ? null : form[k];
    body.isActive = !!form.isActive;
    body.storeName = form.storeName;
    if (isNew) Object.assign(body, { customerCode: form.customerCode, storeNumber: form.storeNumber, channel: form.channel });
    onSave(ref, body);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(520px, 96vw)', background: 'var(--bg-card)', borderLeft: '1px solid var(--border)', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              {isNew ? 'New Store' : form.storeName}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {isNew ? 'Identity fields are fixed once created' : `${form.storeRef} · ${form.channel} · ${form.customerName ?? form.customerCode}`}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>✕</Button>
        </div>

        {!isNew && (
          <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)' }}>
            {!confirmDelete ? (
              <button style={{ ...LINK_BTN, color: 'var(--danger)' }} onClick={() => setConfirmDelete(true)}>
                Delete this store
              </button>
            ) : (
              <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)',
                borderRadius: 'var(--radius)', padding: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--text-primary)', marginBottom: 8 }}>
                  Delete <strong>{form.storeName}</strong> ({form.storeRef})? This also removes its
                  ranging, demographics and banner details. It can be recovered from history, but
                  not from the app.
                </div>
                {deleteError && (
                  <div style={{ fontSize: 11, color: 'var(--danger)', marginBottom: 8 }}>
                    {String(deleteError.message ?? deleteError)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" variant="destructive" disabled={deleting} onClick={() => onDelete(form.storeRef)}>
                    {deleting ? 'Deleting…' : 'Delete store'}
                  </Button>
                  <Button size="sm" disabled={deleting} onClick={() => setConfirmDelete(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {isNew && (
            <section>
              <Label style={{ marginBottom: 8 }}>Identity</Label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><Label>Customer Code *</Label><Input value={form.customerCode ?? ''} onChange={set('customerCode')} placeholder="CUS0004" /></div>
                <div><Label>Store Number *</Label><Input value={form.storeNumber ?? ''} onChange={set('storeNumber')} /></div>
                <div>
                  <Label>Channel *</Label>
                  <Select value={form.channel ?? 'INDEPENDENT'} onChange={set('channel')}>
                    {CHANNELS.map((c) => <option key={c}>{c}</option>)}
                  </Select>
                </div>
              </div>
            </section>
          )}

          <section>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <Label>Store Name *</Label>
                <Input value={form.storeName ?? ''} onChange={set('storeName')} />
              </div>
              <div>
                <Label>Active</Label>
                <Select value={form.isActive ? 'Yes' : 'No'} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === 'Yes' }))}>
                  <option>Yes</option><option>No</option>
                </Select>
              </div>
            </div>
          </section>

          {!isNew && RANGING_CHANNELS.has(form.channel) && <RangingSection storeRef={form.storeRef} />}

          {GROUPS.map(([title, fields]) => (
            <section key={title}>
              <Label style={{ marginBottom: 8 }}>{title}</Label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {fields.map(([k, label]) => (
                  <div key={k}>
                    <Label style={{ textTransform: 'none', letterSpacing: 0 }}>{label}</Label>
                    <Input value={form[k] ?? ''} onChange={set(k)} />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
          {saveError && <span style={{ fontSize: 12, color: 'var(--danger)', flex: 1 }}>{String(saveError.message ?? saveError)}</span>}
          {!saveError && <span style={{ flex: 1 }} />}
          <Button size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="primary" disabled={!canSave || saving} onClick={submit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Ranging section (drawer): per-store SKU authorization toggles ───────── */

function RangingSection({ storeRef }) {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['ranging-store', storeRef],
    queryFn: () => apiFetch(`/ranging/store/${encodeURIComponent(storeRef)}`).then((r) => r.json()),
    staleTime: 60_000,
  });
  const toggle = useMutation({
    mutationFn: async ({ itemNumber, ranged }) => {
      const res = await apiFetch('/ranging', { method: 'PUT', body: JSON.stringify({ storeRef, itemNumber, ranged }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Toggle failed (${res.status})`);
      return res.json();
    },
    onMutate: async ({ itemNumber, ranged }) => {
      await qc.cancelQueries({ queryKey: ['ranging-store', storeRef] });
      qc.setQueryData(['ranging-store', storeRef], (old) =>
        (old ?? []).map((it) => (it.itemNumber === itemNumber ? { ...it, ranged: ranged ? 1 : 0 } : it)));
    },
    onError: () => qc.invalidateQueries({ queryKey: ['ranging-store', storeRef] }),
    onSettled: () => { qc.invalidateQueries({ queryKey: ['ranging-store', storeRef] }); qc.invalidateQueries({ queryKey: ['ranging-matrix'] }); },
  });

  const rangedCount = items.filter((i) => i.ranged).length;
  return (
    <section>
      <Label style={{ marginBottom: 8 }}>Ranging ({rangedCount}/{items.length} SKUs)</Label>
      {isLoading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading ranging…</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items.map((it) => (
          <label key={it.itemNumber} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!it.ranged}
              disabled={toggle.isPending}
              onChange={(e) => toggle.mutate({ itemNumber: it.itemNumber, ranged: e.target.checked })}
            />
            <span style={{ flex: 1 }}>{it.productName ?? it.itemNumber}</span>
            {it.ranged && it.rangedFrom ? (
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>since {fmtDate(it.rangedFrom).split(',')[0]}</span>
            ) : null}
          </label>
        ))}
      </div>
      {toggle.error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{String(toggle.error.message ?? toggle.error)}</div>}
    </section>
  );
}

/* ── Ranging matrix import modal ─────────────────────────────────────────── */

function RangingImportModal({ state, onCommit, onToggleReplace, onClose }) {
  const { fileName, preview, committing, result, error, replace } = state;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 'min(560px, 94vw)', maxHeight: '84vh', overflow: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Ranging import — {fileName}</div>
        {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}
        {!error && !preview && !result && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Comparing matrix against current ranging…</div>}
        {!error && preview && !result && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
              {preview.storesInFile.toLocaleString()} stores in file · <b>{preview.opens}</b> SKUs to range · <b>{preview.closes}</b> to de-range
              {preview.unknownEans?.length ? ` · ${preview.unknownEans.length} unknown EAN columns ignored` : ''}
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!replace} disabled={committing} onChange={(e) => onToggleReplace(e.target.checked)} style={{ marginTop: 2 }} />
              <span><b style={{ color: 'var(--text-body)' }}>Replace mode</b> — treat the file as the full range for each store it covers and de-range any current SKU it doesn't list. Off = only the EANs in the file change.</span>
            </label>
            {preview.sampleOpens?.length > 0 && (
              <div>
                <Label>Ranging (sample)</Label>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, maxHeight: 120, overflow: 'auto', fontSize: 12, color: 'var(--text-body)' }}>
                  {preview.sampleOpens.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {preview.sampleCloses?.length > 0 && (
              <div>
                <Label>De-ranging (sample)</Label>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, maxHeight: 120, overflow: 'auto', fontSize: 12, color: 'var(--text-body)' }}>
                  {preview.sampleCloses.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {preview.errors?.length > 0 && (
              <div>
                <Label style={{ color: 'var(--danger)' }}>Errors (rows skipped)</Label>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, maxHeight: 100, overflow: 'auto', fontSize: 12, color: 'var(--text-body)' }}>
                  {preview.errors.slice(0, 30).map((e2, i) => <li key={i}>Row {e2.row}: {e2.error}</li>)}
                </ul>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" variant="primary" disabled={committing || (preview.opens === 0 && preview.closes === 0)} onClick={onCommit}>
                {committing ? 'Applying…' : 'Apply changes'}
              </Button>
            </div>
          </>
        )}
        {result && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
              Done — {result.opens} ranged, {result.closes} de-ranged, {result.errors?.length ?? 0} errors.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="primary" onClick={onClose}>Close</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Import modal (dry-run diff → commit) ────────────────────────────────── */

function ImportModal({ state, onToggleDeactivate, onCommit, onClose }) {
  const { fileName, preview, committing, result, error, deactivateMissing } = state;
  const list = (items, render) => (
    <ul style={{ margin: '4px 0 0', paddingLeft: 18, maxHeight: 140, overflow: 'auto', fontSize: 12, color: 'var(--text-body)' }}>
      {items.slice(0, 50).map((x, i) => <li key={i}>{render(x)}</li>)}
      {items.length > 50 && <li>… and {items.length - 50} more</li>}
    </ul>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 'min(640px, 94vw)', maxHeight: '86vh', overflow: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Import — {fileName}</div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}
        {!error && !preview && !result && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Analysing file against the database…</div>}

        {!error && preview && !result && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
              {preview.total.toLocaleString()} rows read · <b>{preview.adds.length}</b> new · <b>{preview.changes.length}</b> changed ·{' '}
              {preview.unchanged.toLocaleString()} unchanged · {preview.errors.length} errors
            </div>
            {preview.adds.length > 0 && (
              <div>
                <Label>New stores</Label>
                {list(preview.adds, (a) => `${a.storeRef} — ${a.storeName}`)}
              </div>
            )}
            {preview.changes.length > 0 && (
              <div>
                <Label>Changed</Label>
                {list(preview.changes, (c) => `${c.storeRef} — ${c.storeName}: ${c.fields.join(', ')}`)}
              </div>
            )}
            {preview.errors.length > 0 && (
              <div>
                <Label style={{ color: 'var(--danger)' }}>Errors (rows skipped)</Label>
                {list(preview.errors, (e2) => `Row ${e2.row}: ${e2.error}`)}
              </div>
            )}
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--text-body)', cursor: 'pointer' }}>
              <input type="checkbox" checked={deactivateMissing} onChange={(e) => onToggleDeactivate(e.target.checked)} />
              Deactivate stores missing from this file{preview.deactivations?.length ? ` (${preview.deactivations.length} affected)` : ''} — scoped to the channels present in the file
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" variant="primary" disabled={committing || (preview.adds.length === 0 && preview.changes.length === 0 && !deactivateMissing)} onClick={onCommit}>
                {committing ? 'Importing…' : 'Apply Import'}
              </Button>
            </div>
          </>
        )}

        {result && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
              Done — {result.adds.length} added, {result.changes.length} changed,{' '}
              {result.deactivations?.length ?? 0} deactivated, {result.errors.length} errors.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="primary" onClick={onClose}>Close</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
